import { Injectable } from '@angular/core';
import { looksLikeOrgKey } from '@processpuzzle/auth/domain';

const ORG_KEY_STORAGE_KEY = 'pp.customer.orgKey';

/**
 * Remembers which organization this browser signed up, so the portal has something to open.
 *
 * **Why the portal needs this.** There is no principal on the gateway hop, so nothing tells the
 * portal whose account to show: the organization key has to come from somewhere, and the only
 * alternatives are the URL and the customer's memory. A key written at the end of a successful
 * sign-up is the one moment the browser knows it for certain, so that is when it is written.
 *
 * It is a convenience and not an authorization. Anyone who knows an organization key can read that
 * account today — which is exactly why the portal is behind
 * `platform-subscription.portal.enabled` — so remembering one grants nothing that typing it would
 * not. When nothing is remembered, `OrganizationKeyPromptComponent` asks.
 *
 * **`localStorage`, unlike the draft.** The draft is a half-filled form full of personal and
 * commercial data and lives in `sessionStorage` for the length of one Keycloak round trip. This is
 * one slug the customer chose themselves and will keep using for the life of their tenant; asking
 * for it on every visit would be asking them to remember a value the browser could have kept. The
 * asymmetry is deliberate rather than an inconsistency.
 *
 * Stored values are validated with `looksLikeOrgKey` on the way out. What is in `localStorage` is
 * whatever the last version of this code — or a browser extension, or a stale tab — put there, and a
 * nonsense value would become a path segment in a request. Validating on read rather than trusting
 * the write is what makes an old or foreign value behave as "nothing remembered".
 */
@Injectable({ providedIn: 'root' })
export class RememberedOrgKeyStorage {
  private get storage(): Storage | undefined {
    try {
      return typeof localStorage === 'undefined' ? undefined : localStorage;
    } catch {
      return undefined;
    }
  }

  read(): string | undefined {
    try {
      const stored = this.storage?.getItem(ORG_KEY_STORAGE_KEY) ?? undefined;
      return looksLikeOrgKey(stored) ? stored : undefined;
    } catch {
      return undefined;
    }
  }

  /** Ignores anything that could not be an organization key, rather than storing it to fail later. */
  write(orgKey: string): void {
    if (!looksLikeOrgKey(orgKey)) return;
    try {
      this.storage?.setItem(ORG_KEY_STORAGE_KEY, orgKey);
    } catch {
      // Storage is a convenience; the prompt is the fallback and always works.
    }
  }

  clear(): void {
    try {
      this.storage?.removeItem(ORG_KEY_STORAGE_KEY);
    } catch {
      // As above.
    }
  }
}
